import { useTheme } from 'contexts'
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Animated } from 'react-native'
import ReactPlayer from 'react-player'
import styled from 'styled-components/native'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'

import { getTransformedMedia } from 'utils/files/fileTransformations'

import { VideoProps } from './types'

const StyledAnimatedContainer = styled(Animated.View)`
  position: absolute;
  background-color: black;
  border-radius: 50px;
  width: 35px;
  height: 35px;
  justify-content: center;
  align-items: center;
`

export const Video = forwardRef(
  (
    {
      handleLoaded = () => {},
      source,
      width,
      height,
      autoplay = false,
      loop = true,
      muted = false,
      onProgress,
      hidePlayPauseButton = false,
    }: VideoProps,
    ref,
  ) => {
    const [hasError, setHasError] = useState(false)
    const [isPlaying, setIsPlaying] = useState(autoplay)
    const [hasPlayed, setHasPlayed] = useState(autoplay)
    const [loading, setLoading] = useState(hasPlayed)
    const theme = useTheme()

    useEffect(() => {
      if (hasPlayed) {
        setLoading(true)
        setHasPlayed(autoplay)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source])

    const fadeAnim = useRef(new Animated.Value(isPlaying ? 0 : 1)).current // Hide the play button if the video is playing, otherwise show it

    const transformedSource = useMemo(
      () => getTransformedMedia('video', source, theme.maxWidth),
      [source, theme.maxWidth],
    )

    const transformedPosterSource = useMemo(
      () => getTransformedMedia('poster', source, theme.maxWidth),
      [source, theme.maxWidth],
    )

    useImperativeHandle(ref, () => ({
      handlePlayOrPause: (newIsPlaying: boolean) => {
        handlePlayOrPause(newIsPlaying)
      },
    }))

    useEffect(() => {
      if (isPlaying) {
        // Execute the fade out animation when the video is paused
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 1000, // 1 second to fade out
          useNativeDriver: true,
        }).start()
      } else {
        // Reset the fade animation value back to 0
        fadeAnim.setValue(1)
      }
    }, [fadeAnim, isPlaying])

    const overlayComponent = useMemo(() => {
      switch (true) {
        case loading:
          return <LoadingSpinner />
        case hasError:
          return (
            <Box align='center'>
              <Box margin={{ right: 'md' }}>
                <Icon name='alert-triangle' size={72} color='warn' />
              </Box>
              <Text color='textInverted' align='center'>
                Sorry, we could not find this video. Please try again later.
              </Text>
            </Box>
          )
        default:
          return hidePlayPauseButton ? (
            <></>
          ) : (
            <StyledAnimatedContainer style={{ opacity: fadeAnim }}>
              {!isPlaying ? (
                <Icon name='play-circle' size={47} color='white' />
              ) : (
                <Icon name='pause-circle' size={47} color='white' />
              )}
            </StyledAnimatedContainer>
          )
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, hasError, fadeAnim, isPlaying])

    if (!transformedSource) return <Box />

    const pressableContainerStyle = {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
    }

    const handleReady = (videoPlayer: ReactPlayer) => {
      handleLoaded(videoPlayer)
      setLoading(false)
      setHasError(false)
    }

    const handlePlayOrPause = (newIsPlaying: boolean) => {
      if (!hasPlayed) {
        setHasPlayed(true)
      }
      setIsPlaying(newIsPlaying)
    }

    // If video failed while loading, we want to show an error
    const handleLoadError = () => {
      if (loading) {
        setLoading(false)
        setHasError(true)
      } else {
        setIsPlaying(false)
      }
    }

    return (
      <Box flex={1} backgroundColor='black'>
        <Pressable
          withOpacity={false}
          style={pressableContainerStyle as any}
          onPress={() => {
            handlePlayOrPause(!isPlaying)
          }}
        >
          {!isPlaying ? (
            <ReactPlayer
              light={transformedPosterSource}
              loop={loop}
              url={transformedSource}
              playsinline
              muted={muted}
              progressInterval={1000 / 30} // 30fps
              width={width}
              height={height}
              onReady={handleReady}
              onBuffer={() => {
                setLoading(true)
              }}
              onBufferEnd={() => setLoading(false)}
              onPlay={() => handlePlayOrPause(true)}
              onPause={() => handlePlayOrPause(false)}
              onError={handleLoadError}
            />
          ) : (
            <ReactPlayer
              playing
              loop={loop}
              url={transformedSource}
              playsinline
              muted={muted}
              progressInterval={1000 / 30} // 30fps
              width={width}
              height={height}
              onReady={handleReady}
              onBuffer={() => {
                setLoading(true)
              }}
              onBufferEnd={() => setLoading(false)}
              onProgress={onProgress}
              onPlay={() => handlePlayOrPause(true)}
              onPause={() => handlePlayOrPause(false)}
              onError={handleLoadError}
            />
          )}

          <Box
            position='absolute'
            justifyContent='center'
            align='center'
            width='100%'
            height='100%'
          >
            {overlayComponent}
          </Box>
        </Pressable>
      </Box>
    )
  },
)
